import React from "react";
import { BadgeCheck, AlertTriangle, ThumbsUp, PencilLine, Loader2 } from "lucide-react";
import api from "../../api";
import { useAuth } from "../../context/AuthContext";
import { useLang } from "../../context/LanguageContext";
import { useToast } from "../../hooks/use-toast";

const shortDate = (iso, lang) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    // Day first, month in the language being read: "23 Sep", "23 sept.". Composed by hand because
    // locale date formats disagree on order and we promised shoppers one shape.
    const month = d.toLocaleDateString(lang || "en", { month: "short" });
    return `${d.getDate()} ${month}`;
  } catch {
    return "";
  }
};

export const PriceVerifiedBadge = ({ deal, className = "" }) => {
  const { t, lang } = useLang();
  if (!deal || deal.price == null) return null;
  const disputed = (deal.priceChangeReports || 0) >= 2;
  const base = "inline-flex items-center gap-1 rounded-full font-bold text-[11px] px-2 py-0.5 border";
  if (disputed) {
    return (
      <span data-testid={`price-disputed-badge-${deal.id}`}
        className={`${base} border-rose-400/50 bg-rose-50 text-rose-600 dark:bg-[#3a1620] ${className}`}>
        <AlertTriangle size={11} /> {t("pv.changed")}
      </span>
    );
  }
  if (deal.priceStale) {
    return (
      <span data-testid={`price-stale-badge-${deal.id}`}
        className={`${base} border-[#f5b942]/50 bg-[#fdf2e0] text-[#b45309] dark:bg-[#3a2f10] ${className}`}>
        <AlertTriangle size={11} /> {t("pv.checkFirst")}
      </span>
    );
  }
  return (
    <span data-testid={`price-verified-badge-${deal.id}`} title={t("pv.tooltip")}
      className={`${base} border-[#16a34a]/30 bg-[#e7f6ec] text-[#16a34a] dark:bg-[#16341f] ${className}`}>
      <BadgeCheck size={11} /> {t("pv.verifiedOn", { date: shortDate(deal.priceVerifiedAt, lang) })}
    </span>
  );
};

export const PriceCheckInline = ({ dealId, initial = null }) => {
  const { t } = useLang();
  const { toast } = useToast();
  const [mine, setMine] = React.useState(initial);
  const [busy, setBusy] = React.useState(false);

  const send = async (stillRight) => {
    setBusy(true);
    try {
      await api.post(`/deals/${dealId}/price-check`, { stillRight });
      setMine(stillRight);
      toast({
        title: stillRight ? t("pv.thanksConfirmed") : t("pv.thanksFlagged"),
        description: stillRight ? t("pv.thanksConfirmedMsg") : t("pv.thanksFlaggedMsg"),
      });
    } catch (err) {
      const d = err.response?.data?.detail;
      toast({ title: t("pv.couldNotSave"), description: typeof d === "string" ? d : t("common.tryAgain") });
    } finally {
      setBusy(false);
    }
  };

  if (mine != null) {
    return (
      <p className="text-xs font-semibold text-[#16a34a] mt-2 flex items-center gap-1.5"
        data-testid={`price-inline-done-${dealId}`}>
        <BadgeCheck size={13} /> {mine ? t("pv.youConfirmed") : t("pv.youFlagged")}
      </p>
    );
  }
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2" data-testid={`price-inline-${dealId}`}>
      <span className="text-xs font-semibold text-foreground">{t("pv.wasItRight")}</span>
      <button onClick={() => send(true)} disabled={busy} data-testid={`price-inline-yes-${dealId}`}
        className="inline-flex items-center gap-1 rounded-full border border-[#16a34a]/40 px-2.5 py-1 text-xs font-bold text-[#16a34a] hover:bg-[#e7f6ec] dark:hover:bg-[#16341f] transition-colors disabled:opacity-60">
        {busy ? <Loader2 size={12} className="animate-spin" /> : <ThumbsUp size={12} />} {t("pv.yesRight")}
      </button>
      <button onClick={() => send(false)} disabled={busy} data-testid={`price-inline-no-${dealId}`}
        className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-xs font-bold text-muted-foreground hover:text-rose-500 hover:border-rose-400 transition-colors disabled:opacity-60">
        {busy ? <Loader2 size={12} className="animate-spin" /> : <PencilLine size={12} />} {t("pv.noChanged")}
      </button>
    </div>
  );
};

export const PriceCheckPanel = ({ deal, onUpdate }) => {
  const { t } = useLang();
  const { user } = useAuth();
  const { toast } = useToast();
  const [busy, setBusy] = React.useState(null);
  const [mine, setMine] = React.useState(deal.myPriceCheck ?? null);

  if (deal.price == null) return null;
  const owner = user && user.id === deal.businessId;
  const canVote = user && (user.role === "shopper" || owner);

  const send = async (stillRight) => {
    setBusy(stillRight ? "yes" : "no");
    try {
      const { data } = await api.post(`/deals/${deal.id}/price-check`, { stillRight });
      setMine(stillRight);
      onUpdate?.(data);
      toast({
        title: stillRight ? t("pv.thanksConfirmed") : t("pv.thanksFlagged"),
        description: stillRight ? t("pv.thanksConfirmedMsg") : t("pv.thanksFlaggedMsg"),
      });
    } catch (err) {
      const d = err.response?.data?.detail;
      toast({ title: t("pv.couldNotSave"), description: typeof d === "string" ? d : t("common.tryAgain") });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-secondary/50 p-4" data-testid="price-check-panel">
      <div className="flex flex-wrap items-center gap-2">
        <PriceVerifiedBadge deal={deal} />
        {(deal.priceConfirms || 0) > 0 && (
          <span className="text-xs text-muted-foreground" data-testid="price-confirm-count">
            {t(deal.priceConfirms === 1 ? "pv.countOne" : "pv.countMany", { n: deal.priceConfirms })}
          </span>
        )}
      </div>
      <p className="text-sm text-muted-foreground mt-2">
        {owner ? t("pv.ownerHelp") : t("pv.shopperHelp")}
      </p>
      {!canVote ? (
        <p className="text-xs text-muted-foreground mt-2" data-testid="price-check-signin-hint">{t("pv.signInHint")}</p>
      ) : (
        <div className="flex flex-wrap gap-2 mt-3">
          <button onClick={() => send(true)} disabled={!!busy} data-testid="price-still-right-btn"
            className={`inline-flex items-center gap-2 text-sm font-bold px-4 py-2 rounded-xl transition-colors disabled:opacity-60 ${mine === true ? "bg-[#16a34a] text-white" : "border border-[#16a34a]/40 text-[#16a34a] hover:bg-[#e7f6ec] dark:hover:bg-[#16341f]"}`}>
            {busy === "yes" ? <Loader2 size={15} className="animate-spin" /> : <ThumbsUp size={15} />}
            {t("pv.stillRight")}
          </button>
          {!owner && (
            <button onClick={() => send(false)} disabled={!!busy} data-testid="price-changed-btn"
              className={`inline-flex items-center gap-2 text-sm font-bold px-4 py-2 rounded-xl transition-colors disabled:opacity-60 ${mine === false ? "bg-rose-500 text-white" : "border border-border text-muted-foreground hover:text-rose-500 hover:border-rose-400"}`}>
              {busy === "no" ? <Loader2 size={15} className="animate-spin" /> : <PencilLine size={15} />}
              {t("pv.priceChanged")}
            </button>
          )}
        </div>
      )}
      {mine != null && (
        <p className="text-xs text-muted-foreground mt-2" data-testid="price-check-mine">
          {mine ? t("pv.youConfirmed") : t("pv.youFlagged")}
        </p>
      )}
    </div>
  );
};
